import { useState } from 'react'
import { api } from '@/lib/api'
import { useToast } from '@/context/ToastContext'
import { relativeTime } from '@/lib/format'
import { Switch } from '@/shared/components/Switch'
import s from '@/features/org/components/ConsumptionBlockPanel.module.css'
import { DangerConfirmModal } from '@/shared/components/DangerConfirmModal'
import { ConfirmModal } from '@/shared/components/ConfirmModal'
import type { ConsumableArtefact, ConsumptionBlock } from '@/lib/apiTypes'
import { useOrgConsumptionBlock } from '@/lib/hooks'
import { CONSUMABLE_ARTEFACTS } from '@/lib/apiTypes'

/**
 * The org Danger-zone "block all consumption" kill switch.
 *
 * A master switch over three per-artefact ones. The MASTER CARRIES NO STATE: armed means all three
 * are armed, and flipping it sends `{ all }`. A fourth stored flag would create a state nothing can
 * answer - "master off, providers on" - and two places to look for the truth.
 *
 * ARMING is the destructive direction, not disarming: it breaks every `terraform init` and
 * `terraform apply` in the org for everyone. So arming asks for the org slug to be typed, and
 * disarming only needs a plain confirmation - the usual arrangement is reversed here.
 */

const LABEL: Record<ConsumableArtefact, { title: string; sub: string }> = {
  module:   { title: 'Modules',   sub: 'Refuses every module download, including archives already signed.' },
  provider: { title: 'Providers', sub: 'Refuses every provider download, mirrored and self-built alike.' },
  state:    { title: 'State',     sub: 'Refuses every state read and blocks the lock, so no apply can start.' },
}

type Pending =
  | { kind: 'arm'; target: ConsumableArtefact | 'all'; label: string }
  | { kind: 'disarm'; target: ConsumableArtefact | 'all'; label: string }

/** `orgSlug` is both the API path segment and the type-to-confirm phrase - every org call in this
 *  app addresses the org by slug, and rbacTenant resolves it. */
export function ConsumptionBlockPanel({ orgSlug }: { orgSlug: string }) {
  const { data, loading, error, refetch } = useOrgConsumptionBlock(orgSlug)
  const { showToast } = useToast()
  const [busy, setBusy] = useState<ConsumableArtefact | 'all' | null>(null)
  const [pending, setPending] = useState<Pending | null>(null)

  const armedCount = data ? CONSUMABLE_ARTEFACTS.filter((a) => data[a].blocked).length : 0

  async function apply(target: ConsumableArtefact | 'all', blocked: boolean) {
    setBusy(target)
    try {
      await api.patch<ConsumptionBlock>(`/api/orgs/${orgSlug}/policies/consumption-block`,
        target === 'all' ? { all: blocked } : { [target]: blocked })
      await refetch()
      const what = target === 'all' ? 'All consumption' : `${LABEL[target].title}`
      showToast(blocked ? `${what} blocked.` : `${what} unblocked.`, blocked ? 'success' : 'success')
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setBusy(null)
      setPending(null)
    }
  }

  function request(target: ConsumableArtefact | 'all', blocked: boolean) {
    const label = target === 'all' ? 'all consumption' : LABEL[target].title.toLowerCase()
    setPending({ kind: blocked ? 'arm' : 'disarm', target, label })
  }

  if (loading) return <div className="skeleton" style={{ height: 150, borderRadius: 8 }} />
  if (error || !data) {
    return (
      <p style={{ fontSize: '0.82rem', color: 'var(--text2)', margin: 0 }}>
        Could not load the consumption block: {error ?? 'no data'}
      </p>
    )
  }

  return (
    <div className={s.panel} style={{ marginBottom: 28 }}>
      {/* Master row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16, padding: '14px 16px',
        border: `1px solid ${armedCount > 0 ? 'var(--red)' : 'var(--border)'}`,
        borderRadius: 8, background: 'var(--bg2)',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text)' }}>
            Block all consumption
            {armedCount > 0 && armedCount < CONSUMABLE_ARTEFACTS.length && (
              <span className="badge badge-red" style={{ marginLeft: 8 }}>
                {armedCount} of {CONSUMABLE_ARTEFACTS.length} armed
              </span>
            )}
            {data.all_blocked && <span className="badge badge-red" style={{ marginLeft: 8 }}>ALL BLOCKED</span>}
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text2)', marginTop: 4, lineHeight: 1.5 }}>
            Refuses every download for this organisation. <strong>terraform init and apply will
            fail for everyone</strong> until it is lifted. Each artefact type is backed by a policy,
            so refusals are attributable on the Policies page.
          </div>
        </div>
        <Switch
          checked={data.all_blocked}
          busy={busy === 'all'}
          label="Block all consumption"
          onChange={(next) => request('all', next)}
        />
      </div>

      {/* Per-artefact rows */}
      <div style={{ marginTop: 10, marginLeft: 16 }}>
        {CONSUMABLE_ARTEFACTS.map((artefact) => {
          const entry = data[artefact]
          return (
            <div
              key={artefact}
              style={{
                display: 'flex', alignItems: 'center', gap: 16,
                padding: '10px 0', borderBottom: '1px solid var(--border)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)' }}>
                  {LABEL[artefact].title}
                  {entry.blocked && <span className="badge badge-red" style={{ marginLeft: 8 }}>BLOCKED</span>}
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text2)', marginTop: 3 }}>
                  {LABEL[artefact].sub}
                  {/* changed_at only means "armed at" while it IS armed; once disarmed it is when
                      the switch was turned off, which would read as a lie here. */}
                  {entry.blocked && entry.changed_at && (
                    <> · armed {relativeTime(entry.changed_at)}
                      {entry.changed_by ? ` by ${entry.changed_by}` : ''}</>
                  )}
                </div>
              </div>
              <Switch
                checked={entry.blocked}
                busy={busy === artefact}
                label={`Block all ${artefact} consumption`}
                onChange={(next) => request(artefact, next)}
              />
            </div>
          )
        })}
      </div>

      {pending?.kind === 'arm' && (
        <DangerConfirmModal
          track="org_consumption_block_arm"
          title={pending.target === 'all' ? 'Block all consumption' : `Block all ${pending.label}`}
          confirmLabel={pending.target === 'all' ? 'Block everything' : `Block ${pending.label}`}
          confirmPhrase={orgSlug}
          confirmHint={<>Type the organisation slug <code style={{ color: 'var(--accent)' }}>{orgSlug}</code> to confirm:</>}
          message={
            <>
              {pending.target === 'all'
                ? <>Every module, provider and state request from this organisation will be refused</>
                : <>Every <strong>{pending.label}</strong> request from this organisation will be refused</>}
              {' '}immediately — including runs already in flight and URLs already signed. It takes
              effect at once and needs no rescan, and it overrides every per-artefact exception,
              so a module or workspace that was explicitly allowed will also be refused.
              {pending.target === 'state' || pending.target === 'all'
                ? ' A workspace attestation will not lift it.'
                : ''}
              {' '}You can lift it here at any time.
            </>
          }
          busy={busy !== null}
          onConfirm={() => { void apply(pending.target, true) }}
          onCancel={() => setPending(null)}
        />
      )}
      {pending?.kind === 'disarm' && (
        <ConfirmModal
          track="org_consumption_block_disarm"
          title={pending.target === 'all' ? 'Unblock all consumption' : `Unblock ${pending.label}`}
          confirmLabel={`Unblock ${pending.label}`}
          danger
          message={
            <>
              {pending.label.charAt(0).toUpperCase() + pending.label.slice(1)} will start serving
              again straight away. Any ordinary policy that was already blocking an individual
              artefact still applies — this only lifts the org-wide block.
            </>
          }
          busy={busy !== null}
          onConfirm={() => { void apply(pending.target, false) }}
          onCancel={() => setPending(null)}
        />
      )}
    </div>
  )
}
